import styles from "./CheckoutForm.module.css";

// Payment method picker. Three forces stack:
//   1. Order >= PO threshold → PO required
//   2. Customer on terms     → card hidden (paying card voids terms)
//   3. Credit hold ON        → no payment options; contact AM
//
// `modes` is the result of allowedPaymentModes(customer). `onTerms` is
// a hint so we can show a friendly note when card is hidden.
export default function CheckoutPayment({
  value, onChange, route, threshold, subtotal, formatAmount,
  modes = ["card", "po"], onTerms = false, customer, creditCheck,
  gatewayLabel = "Bolt", captureMode = "auth_capture",
  onBack, onNext,
}) {
  function field(k, v) { onChange({ ...value, [k]: v }); }
  const cardAllowed = modes.includes("card");
  const poAllowed   = modes.includes("po");
  const forcedPO    = route === "po" || !cardAllowed;
  const onHold      = customer?.creditHold === "ON";

  function submit(e) {
    e.preventDefault();
    if (onHold) return;
    if (creditCheck && !creditCheck.ok && value.mode === "po") return;
    if (value.mode === "po" && !value.poNumber.trim()) return;
    onNext?.();
  }

  if (onHold) {
    return (
      <div className={styles.form}>
        <h2 className={styles.section}>Payment Method</h2>
        <div className={styles.error}>
          <strong>Account on credit hold.</strong>
          <p>Please contact your account manager before placing this order.</p>
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.back} onClick={onBack}>← Shipping</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={styles.form}>
      <h2 className={styles.section}>Payment Method</h2>

      {forcedPO ? (
        <div className={styles.poBanner}>
          <strong>Purchase Order required.</strong>
          <span>
            {route === "po"
              ? `This order is ${formatAmount(subtotal)}. Orders at or above ${formatAmount(threshold)} ship against an approved Purchase Order — your finance team will be notified after submission.`
              : onTerms
                ? `This account is on ${customer?.terms || "Net terms"}. Paying with a credit card on a terms account would void your terms — orders ship against a Purchase Order on Net terms instead.`
                : "Card payment is unavailable for this account; please submit as a Purchase Order."}
          </span>
        </div>
      ) : (
        <div className={styles.methods}>
          {cardAllowed && (
            <label className={[styles.method, value.mode === "card" ? styles.methodActive : ""].join(" ")}>
              <input
                type="radio"
                name="pay-mode"
                value="card"
                checked={value.mode === "card"}
                onChange={() => field("mode", "card")}
              />
              <span className={styles.methodLabel}>
                Credit Card / Wallet
                <span className={styles.methodSub}>via {gatewayLabel} · {captureMode === "auth_only" ? "auth only" : "auth + capture"}</span>
              </span>
              <span className={styles.methodPrice}>Charged on submit</span>
            </label>
          )}
          {poAllowed && (
            <label className={[styles.method, value.mode === "po" ? styles.methodActive : ""].join(" ")}>
              <input
                type="radio"
                name="pay-mode"
                value="po"
                checked={value.mode === "po"}
                onChange={() => field("mode", "po")}
              />
              <span className={styles.methodLabel}>
                Purchase Order
                {customer?.terms && <span className={styles.methodSub}>{customer.terms}</span>}
              </span>
              <span className={styles.methodPrice}>Net terms · routes for approval</span>
            </label>
          )}
        </div>
      )}

      {value.mode === "po" && (
        <>
          <Field
            label="PO number"
            value={value.poNumber}
            onChange={(v) => field("poNumber", v)}
            required
          />
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Notes for finance / approver</span>
            <textarea
              value={value.notes || ""}
              onChange={(e) => field("notes", e.target.value)}
              className={styles.textarea}
              rows={3}
            />
          </label>

          {creditCheck && !creditCheck.ok && (
            <div className={styles.error}>
              <strong>Order exceeds available credit.</strong>
              <p>{creditCheck.message}</p>
            </div>
          )}
        </>
      )}

      {value.mode === "card" && (
        <div className={styles.cardStub}>
          You'll enter card details in a secure {gatewayLabel} window after Review.
          {captureMode === "auth_only" && (
            <> Your card will be authorized now and captured when the order ships.</>
          )}
        </div>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.back} onClick={onBack}>← Shipping</button>
        <button type="submit" className={styles.next} disabled={creditCheck && !creditCheck.ok && value.mode === "po"}>
          Review Order →
        </button>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", required = false }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}{required && <em>*</em>}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className={styles.input}
      />
    </label>
  );
}
